import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QueryType } from 'enums/QueryType';
import { environment } from "environments/environment";
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { DynamicQueryModel, SelectPropertyModel, AggregationEnum } from 'models/generated/Models.DynamicQuery';
import { SaveQueryRequestModel } from 'models/generated/Models.Query.SaveQueryRequest';
import { QueryPropertyModel } from 'models/generated/Models.QueryProperty';
import { firstValueFrom } from 'rxjs';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ModelProperty } from 'models/Model';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { TeamPipe } from 'pipes/team.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';

export type QueryResults = TaskActionModel | TaskflowModel | CompanyModel | OrderModel | ProductModel | ProductPlanModel;

@Injectable()
export class QueryService {
	private baseUrl = `${environment.gatewayUrl}/api/queries`;

	constructor(private readonly http: HttpClient, private readonly datePipe: CustomDatePipe, private readonly currencyPipe: CurrencyPipe, private readonly translate: TranslatePipe, private readonly userPipe: UserPipe, private readonly teamPipe: TeamPipe) { }


	getQuery(id: string): Promise<QueryModel> {
		return firstValueFrom(this.http.get<QueryModel>(`${this.baseUrl}/${id}`));
	}

	getQueries(): Promise<QueryModel[]> {
		return firstValueFrom(this.http.get<QueryModel[]>(`${this.baseUrl}`));
	}

	saveQuery(query: QueryModel, queryData: DynamicQueryModel): Promise<QueryModel> {
		const request = new SaveQueryRequestModel();
		request.query = query;
		request.queryData = queryData;
		request.include(r => r.query);
		request.include(r => r.queryData);
		request.queryData.include(qd => qd.filters);
		request.queryData.include(qd => qd.selectProperties);
		request.queryData.filters.forEach(filter => filter.include(f => f.property));
		return firstValueFrom(this.http.post<QueryModel>(`${this.baseUrl}`, request));
	}

	deleteQuery(queryId: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${queryId}`));
	}

	getQueryProperties(queryType: QueryType): Promise<QueryPropertyModel[]> {
		return firstValueFrom(this.http.get<QueryPropertyModel[]>(`${this.baseUrl}/get-query-properties/${queryType}`));
	}

	executeFilterQuery<T>(dynamicQuery: DynamicQueryModel): Promise<T> {
		dynamicQuery.include(query => query.filters);
		dynamicQuery.include(query => query.selectProperties);
		dynamicQuery.filters.forEach(filter => filter.include(f => f.property));
		return firstValueFrom(this.http.post<T>(`${this.baseUrl}/execute-dynamic-query`, dynamicQuery));
	}

	getPropertyDetails(queryType: QueryType, existingSelectProperties: SelectPropertyModel[] = []): ModelPropertyWithDerivedName[] {
		let properties: ModelPropertyWithDerivedName[] = [];
		switch (queryType) {
			case QueryType.TaskAction:
				properties = this.getDerivedNamesFromModel(new TaskActionModel, 1);
				break;
			case QueryType.Taskflow:
				properties = this.getDerivedNamesFromModel(new TaskflowModel, 1);
				break;
			case QueryType.Company:
				properties = this.getDerivedNamesFromModel(new CompanyModel, 1);
				break;
			case QueryType.Order:
				properties = this.getDerivedNamesFromModel(new OrderModel, 1);
				break;
			case QueryType.Product:
				properties = this.getDerivedNamesFromModel(new ProductModel, 1);
				break;
			case QueryType.ProductPlan:
				properties = this.getDerivedNamesFromModel(new ProductPlanModel, 1);
				break;
		}
		const missingProperties = existingSelectProperties.filter(sp => !properties.find(p => p.derivedName == sp.name));
		for (const missingProperty of missingProperties) {
			let propertyNameArray = missingProperty.name.split('.');
			const propertyName = propertyNameArray.pop()?.toLowerCase() ?? missingProperty.name;
			const modelName = propertyNameArray.pop()?.toLowerCase() ?? '';
			properties.push({
				name: missingProperty.name,
				type: missingProperty.type,
				isHidden: missingProperty.isHidden,
				isArray: missingProperty.isArray,
				derivedName: missingProperty.name,
				languageString: this.getPropertyLanguageString(propertyName, modelName)
			});
		}
		return properties;
	}

	getDerivedNamesFromModel<T extends GeneratedModel>(model: T, maxDepth: number, currentDepth: number = 0, prefix: string = ''): ModelPropertyWithDerivedName[] {
		function getModelPropertiesWithRelations<T extends GeneratedModel>(model: T, maxDepth: number, currentDepth: number = 0, prefix: string = '', isArray: boolean | undefined = undefined, traversedObjects: string[] = []): ModelPropertyWithDerivedName[] {
			let returnValue = model.getProperties().map(p => ({ ...p, derivedName: prefix + p.name, isArray: isArray != undefined ? isArray : p.isArray }));

			if (traversedObjects.indexOf(model.constructor.name) != -1)
				return [];
			traversedObjects.push(model.constructor.name);
			if (currentDepth < maxDepth) {
				for (const property of returnValue) {
					let newObj = property.new ? property.new() : null;
					if (newObj) {
						returnValue = returnValue.concat(getModelPropertiesWithRelations(Object.getPrototypeOf(newObj), maxDepth, currentDepth + 1, prefix + property.name + '.', property.isArray, traversedObjects));
					}
				}
			}
			return returnValue;
		}
		let result = getModelPropertiesWithRelations(model, maxDepth, currentDepth, prefix).filter(p => !p.isObject);
		return result;
	}

	getPropertyHeader(queryType: QueryType, property: SelectPropertyModel): string {
		return this.getPropertyLanguageString(property.name, QueryType[queryType!])
	}

	getPropertyValue(result: QueryResults, property: SelectPropertyModel): string {
		function getDeepestPropertyValue(obj: any, prop: string) {
			if (prop.indexOf('.') != -1) {
				return getDeepestPropertyValue(obj[prop.split('.')[0]], prop.slice(prop.indexOf('.') + 1));
			}
			return obj[prop];
		}
		let propertyValue;
		if (property.isArray && property.aggregationType !== undefined) {
			switch (property.aggregationType) {
				case AggregationEnum.Count:
					propertyValue = this.Count(result, property.name);
					break;
				case AggregationEnum.Sum:
					propertyValue = this.Sum(result, property.name);
					break;
				case AggregationEnum.Min:
					propertyValue = this.Min(result, property.name);
					break;
				case AggregationEnum.Max:
					propertyValue = this.Max(result, property.name);
					break;
			}
		}
		else if (!property.isArray) {
			propertyValue = getDeepestPropertyValue(result, property.name);
		}
		if (property.type === 'Date')
			return this.datePipe.transform(propertyValue);
		if (property.type === 'shortDate')
			return this.datePipe.transform(propertyValue, 'shortDate');
		if (property.type === 'currency')
			return this.currencyPipe.transform(propertyValue);
		if (property.type === 'boolean')
			return this.translate.transform('general.' + propertyValue);
		if (property.type === 'user')
			return this.userPipe.transform(propertyValue, 'name');
		if (property.type === 'team')
			propertyValue = this.teamPipe.transform(propertyValue);
		return String(propertyValue);
	}

	private getAggregatedValues(node: any, path: string | string[]): any[] {
		const segments = typeof path === "string" ? path.split(".") : path;

		if (node == null) return [];
		if (Array.isArray(node)) return node.flatMap(n => this.getAggregatedValues(n, segments));
		if (segments.length === 0) return [node];

		const [head, ...rest] = segments;
		return this.getAggregatedValues(node[head], rest);
	};

	private Count(obj: QueryResults, path: string): number {
		return this.getAggregatedValues(obj, path).length;
	}

	private Sum(obj: QueryResults, path: string): number {
		return this.getAggregatedValues(obj, path)
			.filter((v): v is number => typeof v === "number" && !Number.isNaN(v))
			.reduce((acc, v) => acc + v, 0);
	}

	private Min<T>(obj: QueryResults, path: string): T | undefined {
		return this.getAggregatedValues(obj, path).reduce((m, v) => (m === undefined || v < m ? v : m), undefined);
	}

	private Max<T>(obj: QueryResults, path: string): T | undefined {
		return this.getAggregatedValues(obj, path).reduce((m, v) => (m === undefined || v > m ? v : m), undefined);
	}

	setSelectProperty(property: SelectPropertyModel, derivedProperty: ModelPropertyWithDerivedName, isHidden: boolean, aggregationType?: AggregationEnum) {
		property.name = derivedProperty.derivedName;
		property.type = derivedProperty.type ?? 'string';
		property.isHidden = isHidden;
		property.isArray = derivedProperty.isArray ?? false;
		property.aggregationType = property.isArray ? aggregationType : undefined;
	}

	getPropertyLanguageString(propertyName: string, originalModelName: string) {
		const modelName = propertyName.indexOf('.') != -1 ? propertyName.split('.')[propertyName.split('.').length - 2] : originalModelName;
		return modelName.toLowerCase() + '.property.' + propertyName.split('.')[propertyName.split('.').length - 1].toLowerCase();
	}
}

class GeneratedModel {
	getProperties = (): ModelProperty[] => [];
}
interface ModelPropertyWithDerivedName extends ModelProperty {
	derivedName: string;
	isArray: boolean | undefined;
}
